import React, { useCallback, useEffect, useState } from 'react';
import { greenCategories, greenIntakes, greenStockSnapshot, coffeeDispatches } from '../lib/api';
import { Plus, Package, Edit2, Trash2, TrendingUp, Calendar, Download, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { inr } from '../lib/format';
import { downloadPdfReport } from '../lib/pdfReport';

const GreenInventory = () => {
  const [categories, setCategories] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [costs, setCosts] = useState([]);
  const [trendCat, setTrendCat] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCatForm, setShowCatForm] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState({ name: '', description: '', min_stock_kg: '' });
  const [showIntakeForm, setShowIntakeForm] = useState(false);
  const [intakeForm, setIntakeForm] = useState({
    category_id: '',
    quantity_kg: 0,
    cost_per_kg: 0,
    supplier: '',
    intake_date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [snapshotDate, setSnapshotDate] = useState(new Date().toISOString().split('T')[0]);
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  const fetchSnapshot = useCallback(async (dateStr) => {
    setSnapshotLoading(true);
    try {
      const iso = new Date(dateStr + 'T23:59:59Z').toISOString();
      const r = await greenStockSnapshot.get(iso);
      setSnapshot(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setSnapshotLoading(false);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const [c, i, k] = await Promise.all([
        greenCategories.getAll(), greenIntakes.getAll(), coffeeDispatches.getCosts(),
      ]);
      setCategories(c.data);
      setIntakes(i.data);
      setCosts(k.data);
      setTrendCat((prev) => prev || (c.data.length > 0 ? c.data[0].id : ''));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    fetchSnapshot(snapshotDate);
  }, [fetchSnapshot, snapshotDate]);

  const handleCatSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...catForm, min_stock_kg: parseFloat(catForm.min_stock_kg) || 0 };
      if (editingCat) {
        await greenCategories.update(editingCat.id, payload);
      } else {
        await greenCategories.create(payload);
      }
      setShowCatForm(false);
      setEditingCat(null);
      setCatForm({ name: '', description: '', min_stock_kg: '' });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleEditCat = (cat) => {
    setEditingCat(cat);
    setCatForm({ name: cat.name, description: cat.description || '', min_stock_kg: cat.min_stock_kg || '' });
    setShowCatForm(true);
  };

  const handleDeleteCat = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    try {
      await greenCategories.delete(cat.id);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete category');
    }
  };

  const handleIntakeSubmit = async (e) => {
    e.preventDefault();
    try {
      await greenIntakes.create({
        ...intakeForm,
        quantity_kg: parseFloat(intakeForm.quantity_kg),
        cost_per_kg: parseFloat(intakeForm.cost_per_kg) || 0,
        intake_date: new Date(intakeForm.intake_date).toISOString(),
      });
      setShowIntakeForm(false);
      setIntakeForm({
        category_id: '',
        quantity_kg: 0,
        cost_per_kg: 0,
        supplier: '',
        intake_date: new Date().toISOString().split('T')[0],
        notes: '',
      });
      fetchAll();
      fetchSnapshot(snapshotDate);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to log intake');
    }
  };

  const handleDeleteIntake = async (intake) => {
    if (!window.confirm(`Delete this intake of ${intake.quantity_kg}kg? Stock will be reverted.`)) return;
    try {
      await greenIntakes.delete(intake.id);
      fetchAll();
      fetchSnapshot(snapshotDate);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete intake');
    }
  };

  const handleDownloadSnapshotPdf = () => {
    if (!snapshot) return;
    const asOf = format(new Date(snapshot.as_of_date), 'MMM dd, yyyy');
    const monthStart = format(new Date(snapshot.month_start), 'MMM dd, yyyy');
    downloadPdfReport({
      title: 'Green Stock Snapshot',
      subtitle: `As of ${asOf} · Month started ${monthStart}`,
      summary: [
        { label: 'Opening (Month Start)', value: `${snapshot.total_opening_kg.toFixed(2)} kg` },
        { label: '+ Intakes (Period)', value: `${snapshot.total_intake_kg.toFixed(2)} kg` },
        { label: '− Roasts (Period)', value: `${snapshot.total_roast_kg.toFixed(2)} kg` },
        { label: 'Closing (Selected Day)', value: `${snapshot.total_closing_kg.toFixed(2)} kg` },
      ],
      tables: [
        {
          title: 'Per-Category Breakdown',
          head: ['Category', 'Opening (kg)', '+ Intakes (kg)', '− Roasts (kg)', 'Closing (kg)'],
          rows: snapshot.categories.map((c) => [
            c.category_name,
            c.opening_kg.toFixed(2),
            `+${c.intake_kg.toFixed(2)}`,
            `−${c.roast_kg.toFixed(2)}`,
            c.closing_kg.toFixed(2),
          ]),
        },
      ],
      filename: `green-stock-snapshot_${snapshotDate}.pdf`,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  const totalStock = categories.reduce((s, c) => s + (c.current_stock_kg || 0), 0);
  const greenCostFor = (catId) => costs.find((c) => c.category_id === catId)?.green_cost_per_kg || 0;
  const stockValue = categories.reduce((s, c) => s + (c.current_stock_kg || 0) * greenCostFor(c.id), 0);
  const lowStockCats = categories.filter((c) => (c.min_stock_kg || 0) > 0 && (c.current_stock_kg || 0) < c.min_stock_kg);
  const trendData = intakes
    .filter((i) => i.category_id === trendCat && (i.cost_per_kg || 0) > 0)
    .sort((a, b) => new Date(a.intake_date) - new Date(b.intake_date))
    .map((i) => ({ label: format(new Date(i.intake_date), 'MMM dd yy'), cost: i.cost_per_kg }));

  return (
    <div className="space-y-6" data-testid="green-inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Green Coffee Inventory</h1>
          <p className="text-gray-600 mt-1">Manage categories and log new stock intakes</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => { setEditingCat(null); setCatForm({ name: '', description: '' }); setShowCatForm(true); }}
            data-testid="add-category-button"
            className="flex items-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-700 transition shadow"
          >
            <Plus className="w-4 h-4" />
            <span>New Category</span>
          </button>
          <button
            onClick={() => setShowIntakeForm(true)}
            data-testid="log-intake-button"
            disabled={categories.length === 0}
            className="flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:from-amber-600 hover:to-orange-700 transition shadow disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            <span>Log Intake</span>
          </button>
        </div>
      </div>

      {/* Stock Snapshot — opening + as-of-date */}
      <div className="bg-gradient-to-br from-green-600 to-emerald-700 rounded-xl shadow-lg p-5 text-white" data-testid="green-snapshot-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl font-bold">Stock Snapshot</h2>
            <p className="text-white/80 text-sm">
              {snapshot ? `Month started ${format(new Date(snapshot.month_start), 'MMM dd, yyyy')}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 bg-white/15 rounded-lg px-3 py-2">
              <Calendar className="w-4 h-4" />
              <label className="text-sm">As of</label>
              <input
                type="date"
                value={snapshotDate}
                onChange={(e) => setSnapshotDate(e.target.value)}
                data-testid="snapshot-date-input"
                max={new Date().toISOString().split('T')[0]}
                className="bg-transparent border-b border-white/30 text-white text-sm focus:outline-none focus:border-white"
              />
            </div>
            <button
              onClick={handleDownloadSnapshotPdf}
              disabled={!snapshot}
              data-testid="download-snapshot-pdf"
              className="flex items-center gap-1.5 bg-white text-green-700 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-green-50 transition disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              <span>PDF</span>
            </button>
          </div>
        </div>
        {snapshotLoading || !snapshot ? (
          <p className="text-white/80 py-6 text-center text-sm">Loading snapshot…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-white/15 rounded-lg p-3">
                <p className="text-xs uppercase text-white/80">Opening (Month Start)</p>
                <p className="text-xl font-bold mt-1">{snapshot.total_opening_kg.toFixed(2)} kg</p>
              </div>
              <div className="bg-white/15 rounded-lg p-3">
                <p className="text-xs uppercase text-white/80">+ Intakes (Period)</p>
                <p className="text-xl font-bold mt-1 text-emerald-200">+{snapshot.total_intake_kg.toFixed(2)} kg</p>
              </div>
              <div className="bg-white/15 rounded-lg p-3">
                <p className="text-xs uppercase text-white/80">− Roasts (Period)</p>
                <p className="text-xl font-bold mt-1 text-orange-200">−{snapshot.total_roast_kg.toFixed(2)} kg</p>
              </div>
              <div className="bg-white/15 rounded-lg p-3">
                <p className="text-xs uppercase text-white/80">Closing (Selected Day)</p>
                <p className="text-xl font-bold mt-1">{snapshot.total_closing_kg.toFixed(2)} kg</p>
              </div>
            </div>
            {snapshot.categories.length > 0 && (
              <div className="bg-white/10 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-white/10">
                    <tr>
                      <th className="text-left py-2 px-3 font-semibold">Category</th>
                      <th className="text-right py-2 px-3 font-semibold">Opening</th>
                      <th className="text-right py-2 px-3 font-semibold">Intakes</th>
                      <th className="text-right py-2 px-3 font-semibold">Roasts</th>
                      <th className="text-right py-2 px-3 font-semibold">Closing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {snapshot.categories.map((c) => (
                      <tr key={c.category_id} className="border-t border-white/10">
                        <td className="py-2 px-3">{c.category_name}</td>
                        <td className="py-2 px-3 text-right">{c.opening_kg.toFixed(2)}</td>
                        <td className="py-2 px-3 text-right text-emerald-200">+{c.intake_kg.toFixed(2)}</td>
                        <td className="py-2 px-3 text-right text-orange-200">−{c.roast_kg.toFixed(2)}</td>
                        <td className="py-2 px-3 text-right font-bold">{c.closing_kg.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Low stock alerts */}
      {lowStockCats.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3" data-testid="low-stock-banner">
          <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-rose-800 text-sm">Low green stock — time to reorder</p>
            <p className="text-sm text-rose-700 mt-0.5">
              {lowStockCats.map((c) => `${c.name} (${(c.current_stock_kg || 0).toFixed(1)} kg, min ${c.min_stock_kg} kg)`).join(' · ')}
            </p>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <p className="text-sm text-gray-600 font-medium">Total Green Stock</p>
          <p className="text-3xl font-bold text-green-700 mt-1">{totalStock.toFixed(2)} kg</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <p className="text-sm text-gray-600 font-medium">Stock Value</p>
          <p className="text-3xl font-bold text-emerald-600 mt-1" data-testid="green-stock-value">{inr(stockValue)}</p>
          <p className="text-xs text-gray-400 mt-0.5">at weighted-avg intake cost</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <p className="text-sm text-gray-600 font-medium">Categories</p>
          <p className="text-3xl font-bold text-gray-800 mt-1">{categories.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <p className="text-sm text-gray-600 font-medium">Recent Intakes</p>
          <p className="text-3xl font-bold text-amber-600 mt-1">{intakes.length}</p>
        </div>
      </div>

      {/* Categories */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <h2 className="text-lg font-bold text-gray-800">Categories</h2>
          <span className="text-sm text-gray-500">{categories.length} types</span>
        </div>
        {categories.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>No categories yet. Click "New Category" to create your first.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Name</th>
                  <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Description</th>
                  <th className="text-right py-3 px-6 text-sm font-semibold text-gray-700">Current Stock</th>
                  <th className="text-right py-3 px-6 text-sm font-semibold text-gray-700">Min Stock</th>
                  <th className="text-right py-3 px-6 text-sm font-semibold text-gray-700">Avg Cost/kg</th>
                  <th className="text-center py-3 px-6 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <tr key={c.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-6 font-semibold text-gray-800">{c.name}</td>
                    <td className="py-3 px-6 text-sm text-gray-600">{c.description || '-'}</td>
                    <td className="py-3 px-6 text-right font-bold text-green-700">
                      {(c.current_stock_kg || 0).toFixed(2)} kg
                      {(c.min_stock_kg || 0) > 0 && (c.current_stock_kg || 0) < c.min_stock_kg && (
                        <span className="ml-2 inline-block px-2 py-0.5 text-[10px] rounded-full font-bold bg-rose-100 text-rose-700">LOW</span>
                      )}
                    </td>
                    <td className="py-3 px-6 text-right text-sm text-gray-600">{(c.min_stock_kg || 0) > 0 ? `${c.min_stock_kg} kg` : '-'}</td>
                    <td className="py-3 px-6 text-right text-sm text-gray-600">{greenCostFor(c.id) > 0 ? inr(greenCostFor(c.id)) : '-'}</td>
                    <td className="py-3 px-6 text-center">
                      <div className="inline-flex gap-2">
                        <button onClick={() => handleEditCat(c)} className="p-2 text-blue-600 hover:bg-blue-50 rounded" title="Edit">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteCat(c)} data-testid={`delete-category-${c.id}`} className="p-2 text-red-600 hover:bg-red-50 rounded" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent Intakes */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <h2 className="text-lg font-bold text-gray-800">Recent Intakes</h2>
          <TrendingUp className="w-5 h-5 text-amber-600" />
        </div>
        {intakes.length === 0 ? (
          <div className="py-8 text-center text-gray-500 text-sm">No intakes logged yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Date</th>
                  <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Category</th>
                  <th className="text-right py-3 px-6 text-sm font-semibold text-gray-700">Quantity (kg)</th>
                  <th className="text-right py-3 px-6 text-sm font-semibold text-gray-700">Cost/kg</th>
                  <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Supplier</th>
                  <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Notes</th>
                  <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Logged By</th>
                  <th className="text-center py-3 px-6 text-sm font-semibold text-gray-700"></th>
                </tr>
              </thead>
              <tbody>
                {intakes.map((i) => (
                  <tr key={i.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-6 text-sm text-gray-600">{format(new Date(i.intake_date), 'MMM dd, yyyy')}</td>
                    <td className="py-3 px-6 font-medium text-gray-800">{i.category_name}</td>
                    <td className="py-3 px-6 text-right font-semibold">{i.quantity_kg.toFixed(2)}</td>
                    <td className="py-3 px-6 text-right text-gray-600">₹{(i.cost_per_kg || 0).toFixed(2)}</td>
                    <td className="py-3 px-6 text-sm text-gray-600">{i.supplier || '-'}</td>
                    <td className="py-3 px-6 text-sm text-gray-600">{i.notes || '-'}</td>
                    <td className="py-3 px-6 text-sm text-gray-600">{i.entered_by || '-'}</td>
                    <td className="py-3 px-6 text-center">
                      <button onClick={() => handleDeleteIntake(i)} data-testid={`delete-intake-${i.id}`} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Green cost trend */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5" data-testid="green-cost-trend-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-lg font-bold text-gray-800">Bean Cost Trend (₹/kg per intake)</h3>
          <select value={trendCat} onChange={(e) => setTrendCat(e.target.value)}
            data-testid="cost-trend-category-select"
            className="px-3 py-2 border rounded-lg text-sm">
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {trendData.length < 2 ? (
          <p className="text-center text-gray-400 py-8 text-sm">
            Need at least 2 priced intakes for this category to plot a cost trend.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trendData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `₹${v}`} tick={{ fontSize: 11 }} domain={['auto', 'auto']} />
              <Tooltip formatter={(v) => [inr(v), 'Cost/kg']} />
              <Line type="monotone" dataKey="cost" stroke="#10b981" strokeWidth={2.5} dot={{ r: 4, fill: '#10b981' }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Category Modal */}
      {showCatForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">{editingCat ? 'Edit Category' : 'New Category'}</h2>
              <button onClick={() => { setShowCatForm(false); setEditingCat(null); }} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleCatSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text" required value={catForm.name}
                  onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
                  placeholder="e.g. Arabica-Karnataka"
                  data-testid="category-name-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows="2" value={catForm.description}
                  onChange={(e) => setCatForm({ ...catForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Stock Alert (kg)</label>
                <input
                  type="number" step="0.1" min="0" value={catForm.min_stock_kg}
                  onChange={(e) => setCatForm({ ...catForm, min_stock_kg: e.target.value })}
                  placeholder="e.g. 10 — warns when stock drops below this"
                  data-testid="category-min-stock-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div className="flex justify-end gap-3 pt-3 border-t">
                <button type="button" onClick={() => { setShowCatForm(false); setEditingCat(null); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" data-testid="category-save-button" className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold">{editingCat ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Intake Modal */}
      {showIntakeForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Log New Intake</h2>
              <button onClick={() => setShowIntakeForm(false)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleIntakeSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select
                  required value={intakeForm.category_id}
                  onChange={(e) => setIntakeForm({ ...intakeForm, category_id: e.target.value })}
                  data-testid="intake-category-select"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">— select category —</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity (kg) *</label>
                  <input
                    type="number" step="0.01" required
                    value={intakeForm.quantity_kg}
                    onChange={(e) => setIntakeForm({ ...intakeForm, quantity_kg: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cost per kg (₹)</label>
                  <input
                    type="number" step="0.01"
                    value={intakeForm.cost_per_kg}
                    onChange={(e) => setIntakeForm({ ...intakeForm, cost_per_kg: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                <input
                  type="text" value={intakeForm.supplier}
                  onChange={(e) => setIntakeForm({ ...intakeForm, supplier: e.target.value })}
                  placeholder="e.g. Karnataka Estates Co."
                  data-testid="intake-supplier-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Intake Date *</label>
                <input
                  type="date" required value={intakeForm.intake_date}
                  onChange={(e) => setIntakeForm({ ...intakeForm, intake_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  rows="2" value={intakeForm.notes}
                  onChange={(e) => setIntakeForm({ ...intakeForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div className="flex justify-end gap-3 pt-3 border-t">
                <button type="button" onClick={() => setShowIntakeForm(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" data-testid="intake-save-button" className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-semibold">Log Intake</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GreenInventory;
